import React from 'react';
import { useTrainingForm } from '../../../hooks/forms/useTrainingForm';
import { useTranslatedContent } from '../../../hooks/useTranslatedContent';
import FormFields from './fields/FormFields';
import FormMessage from './fields/FormMessage';
import FormStatus from './status/FormStatus';
import Button from '../../common/Button';
import { Send } from 'lucide-react';

const TrainingRequestForm: React.FC = () => {
  const { t } = useTranslatedContent('request');
  const {
    formData,
    loading,
    error,
    success,
    handleSubmit,
    handleChange
  } = useTrainingForm();

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t('fields.name')}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.name}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('fields.email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700">
            {t('fields.company')}
          </label>
          <input
            type="text"
            id="company"
            name="company"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.company}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="participants" className="block text-sm font-medium text-gray-700">
            {t('fields.participants')}
          </label>
          <input
            type="number"
            id="participants"
            name="participants"
            required
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.participants}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="trainingType" className="block text-sm font-medium text-gray-700">
            {t('fields.trainingType')}
          </label>
          <select
            id="trainingType"
            name="trainingType"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.trainingType}
            onChange={handleChange}
          >
            <option value="">{t('fields.trainingType')}</option>
            <option value="administrator">{t('options.administrator')}</option>
            <option value="endUser">{t('options.endUser')}</option>
            <option value="certification">{t('options.certification')}</option>
            <option value="custom">{t('options.custom')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700">
            {t('fields.preferredDate')}
          </label>
          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={formData.preferredDate}
            onChange={handleChange}
          />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
          {t('fields.message')}
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          value={formData.message}
          onChange={handleChange}
        />
      </div>

      <FormStatus 
        error={error}
        success={success}
        successMessage={t('success')}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          icon={Send}
        >
          {loading ? t('sending') : t('submit')}
        </Button>
      </div>
    </form>
  );
};

export default TrainingRequestForm;